import dayjs from 'dayjs';
import { defineMessages, IntlShape } from 'react-intl';

import { AlertLog, BaselineAlert, ClosureReasons } from 'src/types/alerts';
import { createStringFromTemplate } from './general';
import { ALERT_METRIC_ENUM } from 'src/redux/data/constants';
import {
  getLongOutOfBedSuffix,
  getLongOutOfBedTriggerValue,
} from 'src/utils/alertHelpers';

export const messages = defineMessages({
  high: {
    defaultMessage: 'High',
  },
  low: {
    defaultMessage: 'Low',
  },
  bpm: {
    defaultMessage: 'BPM',
  },
  brpm: {
    defaultMessage: 'BrPM',
  },
  hr: {
    defaultMessage: 'HR',
  },
  rr: {
    defaultMessage: 'RR',
  },
  alert: {
    defaultMessage: 'Notification',
  },
  anAverageOf: {
    defaultMessage: 'An average of',
  },
  hours: {
    defaultMessage: 'hours',
  },
  by: {
    defaultMessage: 'by',
  },
  orMore: {
    defaultMessage: 'or more',
  },
  since: {
    defaultMessage: 'since',
  },
  deviatesFromBaseline: {
    defaultMessage: 'deviates from',
  },
  baseline: {
    defaultMessage: 'baseline',
  },
  days: {
    defaultMessage: 'days',
  },
  bedExitDetails: {
    defaultMessage: 'BedExit Since',
  },
  unoccupied: {
    defaultMessage: 'Unoccupied',
  },
  hour: {
    defaultMessage: 'hour',
  },
  minute: {
    defaultMessage: 'minute',
  },
  minutes: {
    defaultMessage: 'minutes',
  },
  for: {
    defaultMessage: 'for',
  },
  bedTimeHoursDiffer: {
    defaultMessage: 'Bed-Time hours differs from last',
  },
  bedExitFrequencyAlertDetails: {
    defaultMessage:
      'BedExit Frequency Notification: {bedExitsCount} times during the pre-defined period',
  },
  positionChangeAlertDetails: {
    defaultMessage:
      'Patient position has not been changed for {positionChangeHours} hour(s)',
  },
  on: {
    defaultMessage: 'on',
  },
  and: {
    defaultMessage: 'and',
  },
  patient: {
    defaultMessage: 'Patient is out of bed for',
  },
  noConsentAlertLogDetails: {
    defaultMessage:
      '{strongStart}Status changed due to No Consent Status {lineBreak}{strongEnd}',
  },
});

const formatDuration = (duration: number): string => {
  if (duration < 60) {
    return `${duration} ${duration === 1 ? 'second' : 'seconds'}`;
  }

  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;

  return `${minutes}${
    seconds ? `:${seconds < 10 ? `0${seconds}` : seconds}` : ''
  } ${minutes > 1 ? 'minutes' : 'minute'}`;
};

const calculateSinceTime = (
  averageIntervalInSec: number,
  startTime: string,
): string =>
  dayjs(startTime).subtract(averageIntervalInSec, 'second').format('HH:mm');

export const getBaselineAlertDetails = (
  alert: Pick<
    BaselineAlert,
    | 'thresholdMetric'
    | 'thresholdPreposition'
    | 'deviationHours'
    | 'deviationValue'
    | 'baselineValue'
    | 'baselineDaysInterval'
    | 'deviationPercentage'
    | 'reminderCount'
    | 'startTime'
  >,
  intl: IntlShape,
): string => {
  const {
    thresholdMetric,
    thresholdPreposition,
    deviationHours,
    deviationValue,
    baselineValue,
    baselineDaysInterval,
    deviationPercentage,
    reminderCount,
    startTime,
  } = alert;
  const metricMessage =
    thresholdMetric === ALERT_METRIC_ENUM.HR_BASELINE
      ? messages.hr
      : messages.rr;
  const unitMessage =
    thresholdMetric === ALERT_METRIC_ENUM.HR_BASELINE
      ? messages.bpm
      : messages.brpm;

  const reminderCountText = reminderCount
    ? `| ${intl.formatMessage(messages.alert)} #${reminderCount}`
    : '';

  const sinceTime = deviationHours
    ? calculateSinceTime(Math.round(deviationHours * 60 * 60), startTime)
    : '';

  const isValidSinceTime = sinceTime !== 'Invalid Date';

  const details = createStringFromTemplate(
    `{prepositionIndicator} {metric} - {anAverageOf} {deviationHours} {hours} ({deviationValue} {unit}) {deviatesFromBaseline} {baselineDaysInterval} {days} {baseline} ({baselineValue} {unit}) {by} {deviationPercentage}% {orMore} {since} {sinceTime} {reminderCountText}`,
    {
      prepositionIndicator: intl.formatMessage(
        thresholdPreposition === 'ABOVE' ? messages.high : messages.low,
      ),
      metric: intl.formatMessage(metricMessage),
      anAverageOf: intl.formatMessage(messages.anAverageOf),
      hours: intl.formatMessage(messages.hours),
      deviationHours: deviationHours?.toString() || '',
      deviatesFromBaseline: intl.formatMessage(messages.deviatesFromBaseline),
      deviationValue: deviationValue
        ? Math.round(deviationValue).toString()
        : '',
      unit: intl.formatMessage(unitMessage),
      by: intl.formatMessage(messages.by),
      baseline: intl.formatMessage(messages.baseline),
      days: intl.formatMessage(messages.days),
      baselineValue: baselineValue ? Math.round(baselineValue).toString() : '',
      baselineDaysInterval: baselineDaysInterval
        ? Math.round(baselineDaysInterval).toString()
        : '',
      deviationPercentage: deviationPercentage
        ? Math.floor(Math.abs(deviationPercentage)).toString()
        : '',
      orMore: intl.formatMessage(messages.orMore),
      since: isValidSinceTime ? intl.formatMessage(messages.since) : '',
      sinceTime:
        isValidSinceTime && deviationHours
          ? calculateSinceTime(Math.round(deviationHours * 60 * 60), startTime)
          : '',
      reminderCountText: reminderCountText,
    },
  );

  return details;
};

export const getAlertLogDetails = (
  alert: AlertLog,
  intl: IntlShape,
): string => {
  const {
    triggerValue,
    thresholdMetric,
    thresholdPreposition,
    averageInterval,
    thresholdValue,
    averageValue,
    startTime,
    firstMeasurementTime,
    closureReason,
  } = alert;
  const detailsPrefix =
    closureReason === ClosureReasons.PATIENT_CONSENT_DISABLED
      ? intl.formatMessage(messages.noConsentAlertLogDetails, {
          lineBreak: '<br />',
          strongStart: '<strong>',
          strongEnd: '</strong>',
        })
      : '';
  if (!thresholdMetric || thresholdMetric === ALERT_METRIC_ENUM.BED_EXIT) {
    return `${detailsPrefix}${intl.formatMessage(messages.bedExitDetails)} ${dayjs(
      startTime,
    ).format('HH:mm')}`;
  }

  if (
    !thresholdMetric ||
    thresholdMetric === ALERT_METRIC_ENUM.LONG_OUT_OF_BED
  ) {
    const longOutOfBedTriggerValue = getLongOutOfBedTriggerValue(triggerValue);
    const longOutOfBedTriggerValueSuffix = getLongOutOfBedSuffix(triggerValue);

    return `${detailsPrefix}${intl.formatMessage(messages.patient)}
     ${longOutOfBedTriggerValue}
      ${intl.formatMessage(longOutOfBedTriggerValueSuffix)}
       ${intl.formatMessage(messages.orMore)} ${intl.formatMessage(messages.since)}
        ${dayjs(firstMeasurementTime).format('HH:mm')}`;
  }

  if (
    !thresholdMetric ||
    thresholdMetric === ALERT_METRIC_ENUM.BED_EXIT_FREQUENCY
  ) {
    return `${detailsPrefix}${intl.formatMessage(messages.bedExitFrequencyAlertDetails, { bedExitsCount: triggerValue })}`;
  }

  if (
    thresholdMetric === ALERT_METRIC_ENUM.HR_BASELINE ||
    thresholdMetric === ALERT_METRIC_ENUM.RR_BASELINE
  ) {
    return `${detailsPrefix}${getBaselineAlertDetails(alert, intl)}`;
  }

  if (thresholdMetric === ALERT_METRIC_ENUM.POSITION_CHANGE) {
    return `${detailsPrefix}${intl.formatMessage(messages.positionChangeAlertDetails, { positionChangeHours: triggerValue })}`;
  }

  if (!thresholdPreposition || !thresholdValue) {
    return `${detailsPrefix}`;
  }

  if (
    thresholdMetric === ALERT_METRIC_ENUM.HR ||
    thresholdMetric === ALERT_METRIC_ENUM.RR
  ) {
    return `${detailsPrefix}${thresholdMetric} - An average of ${formatDuration(
      averageInterval,
    )} is ${thresholdPreposition?.toLowerCase()} ${thresholdValue} ${intl.formatMessage(
      thresholdMetric === ALERT_METRIC_ENUM.HR ? messages.bpm : messages.brpm,
    )} (${Math.round(averageValue)}) 
      since ${calculateSinceTime(averageInterval, startTime)}`;
  }

  return `${detailsPrefix}`;
};
